/**
 * SysOps Panel API client factory.
 *
 * Builds a single `@retail-ops/api-client` `ApiClient` wired against the
 * mypcb-tenant MSAL token + base URL. NO Teams SDK — SysOps is browser-only.
 *
 * Per Bible §3.0: function key is sent in `x-functions-key` header only.
 */

import { ApiClient } from '@retail-ops/api-client';
import { getMsalToken } from '@/auth/msal';
import { APP_VERSION } from '@/constants/version';

const CLIENT_ID = 'sysops-panel';
const CLIENT_VERSION = `sysops-panel@${APP_VERSION}`;

export interface ApiClientFactoryOptions {
  baseUrl: string;
  functionKey: string;
  clientId?: string;
}

export function createApiClient(options: ApiClientFactoryOptions): ApiClient {
  const clientId = options.clientId ?? CLIENT_ID;
  return new ApiClient({
    baseUrl: options.baseUrl,
    functionKey: options.functionKey,
    version: CLIENT_VERSION,
    getToken: async () => {
      try {
        return await getMsalToken();
      } catch {
        return undefined;
      }
    },
    context: () => ({ clientId }),
  });
}

export function apiClientFactoryOptionsFromEnv(): ApiClientFactoryOptions {
  const baseUrl =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  const functionKey =
    (import.meta.env.VITE_FUNCTION_KEY as string | undefined) ?? '';

  if (!baseUrl && import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.error(
      '[sysops-panel] VITE_API_BASE_URL is not set. The app cannot reach the Function.',
    );
  }

  return {
    baseUrl: baseUrl || 'https://pcb-retail-api.azurewebsites.net/api',
    functionKey,
  };
}
